import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

function fmtInr(v) {
  const n = Number(v || 0);
  return "₹" + n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function PublicQuoteView() {
  const { token } = useParams();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState("");
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND}/api/trade/quotes/share/${token}`);
        setQ(data);
      } catch (e) { setErr(e.response?.data?.detail || "Quote not found"); }
    })();
  }, [token, BACKEND]);

  if (err) return <div className="min-h-screen flex items-center justify-center bg-gray-100"><div className="text-gray-600 font-mono">{err}</div></div>;
  if (!q) return <div className="min-h-screen flex items-center justify-center bg-gray-100"><div className="text-gray-500">Loading…</div></div>;

  const per_bag = Number(q.grand_total || 0) * Number(q.bag_size_kg || 25);

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4 public-quote-scope">
      <style>{`.public-quote-scope, .public-quote-scope * { color: #0B0B0C; } .public-quote-scope .gold { color: #8A6E3A; } .public-quote-scope .accent { color: #C8A97E; } .public-quote-scope .muted { color: #6B7280; } .public-quote-scope .amber { color: #B45309; }`}</style>
      <div className="max-w-3xl mx-auto bg-white p-10 shadow-lg" data-testid="public-quote-view">
        <div className="flex items-start justify-between border-b-2 border-[#C8A97E] pb-4 mb-6">
          <div>
            <div className="text-xs tracking-widest accent uppercase font-mono">Explora ETrades Pvt Ltd</div>
            <div className="mt-1 text-sm muted">SIGA Coffee · Speciality Coffee · Farm to Cup</div>
          </div>
          <div className="text-right">
            <div className="text-xs tracking-widest accent uppercase font-mono">Quotation</div>
            <div className="mt-1 font-mono text-lg font-bold">{q.quote_no}</div>
          </div>
        </div>

        <h1 className="font-serif text-3xl mt-2 tracking-tight">{q.sku_name}</h1>
        <div className="text-sm muted mt-1">{q.sku_type_name}</div>

        <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
          <div><span className="muted uppercase tracking-widest text-[10px] font-mono">Quote Date</span><div>{q.quote_date}</div></div>
          <div><span className="muted uppercase tracking-widest text-[10px] font-mono">Prepared by</span><div>{q.prepared_by_name || "—"}</div></div>
          <div><span className="muted uppercase tracking-widest text-[10px] font-mono">Valid Until</span><div className="amber font-medium">{q.valid_until}</div></div>
          <div><span className="muted uppercase tracking-widest text-[10px] font-mono">Prepared for</span><div>{q.client_name || "—"}</div></div>
        </div>

        <table className="w-full mt-8 text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest muted font-mono border-b-2 border-[#C8A97E]">
              <th className="text-left py-2 w-10">#</th>
              <th className="text-left py-2">Line Item</th>
              <th className="text-right py-2 w-32">Amount (₹/kg)</th>
            </tr>
          </thead>
          <tbody>
            {(q.client_lines || []).map((ln, i) => (
              <tr key={ln.line_id} className="border-b border-gray-100">
                <td className="py-2 muted font-mono text-xs">{i + 1}</td>
                <td className={`py-2 ${ln.kind === "total" ? "font-bold" : ""}`}>{ln.label}</td>
                <td className={`py-2 text-right font-mono ${ln.kind === "total" ? "font-bold gold" : ""}`}>{fmtInr(ln.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-8 flex justify-end">
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest muted font-mono">Grand Total</div>
            <div className="font-serif text-4xl font-bold gold mt-1">{fmtInr(q.grand_total)} <span className="text-lg muted font-mono">/ kg</span></div>
            {q.display_mode === "per_bag" && (
              <div className="mt-2 text-sm muted">
                Per {q.bag_size_kg}kg bag: <span className="font-bold">{fmtInr(per_bag)}</span>
              </div>
            )}
          </div>
        </div>

        {q.notes && <div className="mt-8 pt-4 border-t border-gray-200"><div className="text-[10px] uppercase tracking-widest muted font-mono">Notes</div><div className="mt-1 text-sm">{q.notes}</div></div>}

        <div className="mt-10 pt-4 border-t border-gray-200 text-[10px] muted font-mono leading-relaxed">
          This is a computer-generated estimate. Prices are indicative and subject to change based on market conditions. Terms &amp; taxes apply as per contract.
        </div>
      </div>
    </div>
  );
}
